import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q39i6e3oq4jqumtfpg';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Your message has been sent successfully!');
      form.reset();
      setCharCount(0);
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Your message has been sent successfully! We\'ll get back to you shortly.');
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'An error occurred, please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('A connection error occurred, please try again.');
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-600 mb-3">Contact</span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
            Get in Touch
          </h2>
          <p className="text-base text-foreground-600 max-w-xl mx-auto">
            Reach out to discuss your project, explore collaboration opportunities, or ask us anything.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-5xl mx-auto">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                <i className="ri-mail-line text-xl" style={{ color: 'oklch(var(--accent-600))' }}></i>
              </div>
              <h3 className="text-base font-bold font-heading text-foreground-900 mb-1">Email</h3>
              <p className="text-sm text-foreground-600">info@corelooppartners.com</p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
                <i className="ri-map-pin-line text-xl" style={{ color: 'oklch(var(--primary-600))' }}></i>
              </div>
              <h3 className="text-base font-bold font-heading text-foreground-900 mb-1">Location</h3>
              <p className="text-sm text-foreground-600">Istanbul / Ankara / Izmir<br />Turkey</p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 mb-4">
                <i className="ri-linkedin-fill text-xl" style={{ color: 'oklch(var(--secondary-600))' }}></i>
              </div>
              <h3 className="text-base font-bold font-heading text-foreground-900 mb-1">LinkedIn</h3>
              <p className="text-sm text-foreground-600">Coreloop Partners</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Full Name"
                    className="w-full text-sm px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="example@email.com"
                    className="w-full text-sm px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">Studio / Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  placeholder="Your studio or company name"
                  className="w-full text-sm px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Your Message <span className="text-foreground-400 font-normal">({charCount}/500)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us about your project or consulting needs..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full text-sm px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                ></textarea>
              </div>

              <div className="honeypot-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                className="w-full text-sm font-semibold px-6 py-3.5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Send Message <i className="ri-send-plane-line ml-2"></i>
              </button>

              {formStatus !== 'idle' && (
                <div
                  className={`mt-4 text-sm font-medium text-center p-3 rounded-lg ${
                    formStatus === 'success'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}